import React, { useState, useMemo, useEffect } from 'react';
import SearchBar from './SearchBar';
import ColumnSelectDropdown from './ColumnSelectDropdown';
import TableComponent from './TableComponent';
import FilterIconDrawer from './FilterIconDrawer';
import { Box, VStack, HStack, Flex,IconButton } from '@chakra-ui/react';
import { RepeatIcon } from '@chakra-ui/icons';
import Card from './Card';
import StyledButtonBase from './StyledButtonBase';

const FilterableTable = ({ data, config,tripsData, modalData,...props }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedColumn, setSelectedColumn] = useState('');
  const [filteredData, setFilteredData] = useState(data);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    // Initialize all columns as visible by default
    return config.columns.reduce((acc, column) => {
      acc[column.id] = true; // Default all columns to be visible
      return acc;
    }, {});
  });

  // Create a map of column titles to their IDs
  const titleToIdMap = useMemo(
    () =>
      config.columns.reduce((acc, column) => {
        acc[column.title] = column.id;
        return acc;
      }, {}),
    [config.columns]
  );

  // Create column names and visibility status from the config
  const columnNames = useMemo(() => config.columns.map((col) => col.title), [config.columns]);
  const columnMap = useMemo(() => {
    return config.columns.reduce((acc, column) => {
      acc[column.id] = column; // Map columns by their ID
      return acc;
    }, {});
  }, [config.columns]);

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  // Filter data based on columns defined in the config
  const dataMatchingConfigColumns = useMemo(() => {
    const configColumnIds = config.columns.map((col) => col.id);
    return data.map((row) =>
      Object.fromEntries(
        Object.entries(row).filter(([key]) => configColumnIds.includes(key))
      )
    );
  }, [data, config.columns]);

  // Handle search functionality
  const handleSearch = (value) => {
    setSearchTerm(value);

    if (value) {
      const filtered = data.filter((row) => {
        if (selectedColumn) {
          const columnId = titleToIdMap[selectedColumn]; // Map the selected title to the column ID
          const cellValue = row[columnId]?.toString().toLowerCase();
          return cellValue?.includes(value.toLowerCase());
        } else {
          // Search across all columns if no column is selected
          return Object.values(row).some((cell) =>
            cell?.toString().toLowerCase().includes(value.toLowerCase())
          );
        }
      });
      setFilteredData(filtered);
    } else {
      setFilteredData(data);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilteredData(data);
  };

  // Handle column selection for filtering
  const handleColumnSelect = (columnTitle) => {
    setSelectedColumn(columnTitle);
    const columnId = titleToIdMap[columnTitle]; // Map the selected title to the column ID
    if (searchTerm) {
      const filtered = data.filter((row) => {
        const cellValue = row[columnId]?.toString().toLowerCase();
        return cellValue?.includes(searchTerm.toLowerCase());
      });
      setFilteredData(filtered);
    } else {
      setFilteredData(data);
    }
  };

  // Toggle column visibility based on user selection
  const toggleColumnVisibility = (columnId) => {
    setVisibleColumns((prev) => {
      const updatedColumns = { ...prev, [columnId]: !prev[columnId] };
      if (!updatedColumns[columnId]) {
        delete updatedColumns[columnId]; // Remove column when unchecked
      }
      return updatedColumns;
    });
  };

// Apply filters to data based on config and user input
const applyFilters = (filters) => {
  let newData = data;

  // Apply each filter
  Object.entries(filters).forEach(([column, value]) => {
    newData = newData.filter((row) => {
      // Check if the row value matches the filter value; adjust for formatting differences
      return row[column]?.toString().toLowerCase() === value.toLowerCase();
    });
  });

  setFilteredData(newData);
};


  // Filter the columns to only include visible ones
  const visibleConfigColumns = useMemo(() => {
    return config.columns.filter((column) => visibleColumns[column.id]);
  }, [config.columns, visibleColumns]);

  // Map the filtered data to respect visible columns
  const filteredVisibleData = useMemo(
    () =>
      filteredData.map((row) =>
        Object.fromEntries(
          Object.entries(row).filter(
            ([key]) => visibleColumns[key] // Filter only visible columns
          )
        )
      ),
    [filteredData, visibleColumns]
  );

  // Update data when edited in the TableComponent
  const handleDataEdit = (updatedRow, rowIndex, columnId) => {
    if (columnMap[columnId]?.editable) {
      // Update the filteredData state with the edited row
      const updatedData = [...filteredData];
      updatedData[rowIndex] = { ...updatedData[rowIndex], [columnId]: updatedRow[columnId] };
      setFilteredData(updatedData);
    }
  };

  const resetColumnSelection = () => {
    setSelectedColumn('');
  };

  return (
    <Card
      title={props.cardTitle || ''}
      style={{
        width: props.cardWidth,
        height: props.cardHeight,
        marginBottom: props.cardMarginBottom,
        backgroundColor: props.cardBg,
        ...props,
      }}
    >
      <Box padding={props.cardPadding}>
        <VStack spacing={4} width="100%" alignItems="flex-start">
          <Flex
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            mb={2}
          >
            <HStack spacing={2} flexGrow={1} minWidth="400px">
              <ColumnSelectDropdown
                label={selectedColumn || 'Columns'}
                columns={config.columns} // Pass the full column configuration
                visibleColumns={visibleColumns}
                onToggle={toggleColumnVisibility}
                onSelect={(column) => setSelectedColumn(column)}
                selectMode="filter"
                bg={props.dropdownBg}
                color={props.dropdownColor}
                size={props.dropdownSize}
              />
             <IconButton
              onClick={resetColumnSelection}
              size="sm"
              bg={props.filterDrawerBg}
              color={props.filterdropdownTextColor} 
              icon={<RepeatIcon />}
              />
              <Box width="100%" maxWidth="300px">
                <SearchBar
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  onClear={handleClearSearch}
                  placeholder={`Search ${selectedColumn}`|| 'Search all columns'}
                />
              </Box>
            </HStack>

            <HStack spacing={2} flexGrow={1} minWidth="300px" justifyContent="flex-end">
              <ColumnSelectDropdown
                label={'Columns'}
                columns={config.columns} // Pass the full column configuration
                visibleColumns={visibleColumns}
                onToggle={toggleColumnVisibility}
                onSelect={handleColumnSelect}
                selectMode="visibility"
                bg={props.dropdownBg}
                color={props.dropdownColor}
                size={props.dropdownSize}
              />

              <FilterIconDrawer
                data={dataMatchingConfigColumns} // Pass only the data that matches columns in config
                columns={config.columns} // Pass the column configuration
                onApplyFilters={applyFilters}
                drawerWidth="25vw"
                filterdropdownBg={props.filterdropdownBg}
                filterdropdownTextColor={props.filterdropdownTextColor}
                filterIconColor={props.filterIconColor}
                filterDrawerBg={props.filterDrawerBg}
              />
            </HStack>
          </Flex>

          <Box width="100%" style={{ overflow: 'visible' }}>
            <TableComponent
              data={filteredVisibleData}
              config={{ ...config, columns: visibleConfigColumns }} // Pass only the visible columns
              onEdit={handleDataEdit} // Handle edits through the TableComponent
              tripsData={tripsData} // Pass trips data if available
              modalData={modalData} // Pass modal data if availableata} // Pass modal data if available
              {...props}
            />
          </Box>
        </VStack>
      </Box>
    </Card>
  );
};

export default FilterableTable;
